import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { FiX, FiShield, FiCheck, FiX as FiXIcon } from 'react-icons/fi';
import { internalUrl } from './siteConfig';
import { loadGtag } from './analytics';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const cookieChoice = localStorage.getItem('cookieChoice');
    if (!cookieChoice) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieChoice', 'accepted');
    localStorage.setItem('cookieTimestamp', new Date().toISOString());
    setShowBanner(false);
    loadGtag();
  };

  const handleReject = () => {
    localStorage.setItem('cookieChoice', 'rejected');
    localStorage.setItem('cookieTimestamp', new Date().toISOString());
    setShowBanner(false);
  };

  const cookieCategories = [
    {
      name: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function properly.',
      required: true,
      examples: ['Session management', 'Security features', 'Basic functionality']
    },
    {
      name: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website.',
      required: false,
      examples: ['Page views', 'User behavior', 'Performance metrics']
    },
    {
      name: 'Marketing Cookies',
      description: 'These cookies are used to deliver relevant advertisements.',
      required: false,
      examples: ['Ad targeting', 'Social media integration', 'Remarketing']
    }
  ];

  return (
    <AnimatePresence>
      {showBanner && (
        <m.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="cookie-banner show max-h-[90vh] overflow-y-auto"
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FiShield className="text-white text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">
                      We use cookies to enhance your experience
                    </h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      We use cookies and similar technologies to provide, protect, and improve our services. 
                      By clicking "Accept All", you consent to our use of cookies for analytics and marketing purposes.
                    </p>
                  </div>
                </div>

                {showDetails && (
                  <m.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 space-y-4"
                  >
                    {cookieCategories.map((category, index) => (
                      <div key={category.name} className="bg-white/10 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white">{category.name}</h4>
                          {category.required && (
                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-gray-300 text-sm mb-2">{category.description}</p>
                        <div className="text-xs text-gray-400">
                          <span className="font-semibold text-gray-300">Examples:</span> {category.examples.join(', ')}
                        </div>
                      </div>
                    ))}
                  </m.div>
                )}

                <div className="flex flex-wrap gap-4 mt-4">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-indigo-300 hover:text-white text-sm transition-colors"
                  >
                    {showDetails ? 'Hide Details' : 'Learn More'}
                  </button>
                  <a
                    href={internalUrl('site-footer')}
                    className="text-indigo-300 hover:text-white text-sm transition-colors"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full sm:w-auto mt-4 lg:mt-0">
                <m.button
                  onClick={handleReject}
                  className="btn bg-transparent border-2 border-gray-600 text-white hover:bg-gray-600"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiXIcon className="mr-2" />
                  Reject All
                </m.button>
                <m.button
                  onClick={handleAccept}
                  className="btn btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiCheck className="mr-2" />
                  Accept All
                </m.button>
              </div>

              <m.button
                type="button"
                onClick={handleReject}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                aria-label="Reject optional cookies and close banner"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX className="text-xl" />
              </m.button>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner; 